/**
 * Действия режима тренировки переключения раскладок.
 *
 * Занятие целиком — один прогон набора: цикл Memory Loop здесь не нужен,
 * переход тренируется повторением, а не стадиями припоминания.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { layoutSwitchReady } from '../state/script-readiness.js';
import { finishLayoutSwitch, layoutSwitchScreen } from '../state/layout-switch.js';

/** Действия режима, которые приложение передаёт экранам. */
export interface LayoutSwitchActions {
  readonly onStartLayoutSwitch: () => void;
  readonly onLayoutSwitchDone: (run: TypingSession) => void;
}

/** Что нужно режиму от приложения. */
export interface LayoutSwitchDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useLayoutSwitch(runtime: Runtime, deps: LayoutSwitchDeps): LayoutSwitchActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartLayoutSwitch = useCallback(() => {
    if (workspace === null) return;

    // Упражнение идёт в двух раскладках: не освоив обеих, тренировать переход
    // не с чего — цена перехода утонула бы в незнакомых буквах. Объяснение
    // открывается вместо занятия, а не вместо ответа.
    if (!layoutSwitchReady(workspace)) {
      setScreen({ name: 'locked-mode', target: { kind: 'layout-switch' } });
      return;
    }

    setScreen(layoutSwitchScreen(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  const onLayoutSwitchDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'layout-switch') return;

      const transition = finishLayoutSwitch(runtime, workspace, screen.plan, run, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartLayoutSwitch, onLayoutSwitchDone };
}
